// apps/web/src/components/shell/navegacao.ts — Fase 06, Plano 05 (D-04..D-08).
//
// Modelo declarativo da navegação da aplicação industrial.
//
// AVISO DE SEGURANÇA (T-6-02 / ASVS V5):
// O filtro de visibilidade neste módulo é exclusivamente para "privilégio mínimo visual"
// (D-05) e ergonomia (não exibir ao operador caminhos inacessíveis).
// A AUTORIZAÇÃO REAL vive e continuará vivendo nos @RequirePermission e guards
// do backend NestJS. Ocultar um item aqui NÃO autoriza nem protege dados.

export interface ItemNavegacao {
  readonly id: string;
  readonly rotulo: string;
  readonly caminho: string;
  readonly permissaoExigida?: string;
}

export interface GrupoNavegacao {
  readonly id: string;
  readonly rotulo: string;
  readonly iconeNome: string;
  readonly caminho?: string;
  readonly permissaoExigida?: string;
  readonly itens?: readonly ItemNavegacao[];
}

/** A fronteira de segmento evita confundir /pedidos com /pedidos-antigos. */
export function resolverDestinoNavegacao(pathname: string, grupos: readonly GrupoNavegacao[] = ARVORE_NAVEGACAO) {
  const destinos = grupos.flatMap((grupo) => [
    ...(grupo.caminho ? [{ grupo, item: undefined, caminho: grupo.caminho }] : []),
    ...(grupo.itens?.map((item) => ({ grupo, item, caminho: item.caminho })) ?? []),
  ]);
  return destinos.filter(({ caminho }) => pathname === caminho || pathname.startsWith(`${caminho}/`))
    .sort((a, b) => b.caminho.length - a.caminho.length)[0];
}

export interface BreadcrumbSegment {
  readonly label: string;
  readonly href?: string;
  readonly isCurrent: boolean;
}

export function resolverBreadcrumbs(pathname: string, search = ''): readonly BreadcrumbSegment[] {
  const destino = resolverDestinoNavegacao(pathname);
  if (!destino) return pathname.split('/').filter(Boolean).map((parte, i, partes) => ({
    label: parte.charAt(0).toUpperCase() + parte.slice(1).replace(/-/g, ' '), isCurrent: i === partes.length - 1,
  }));
  const { grupo, item, caminho } = destino;
  const filho = pathname !== caminho;
  const segmentos: BreadcrumbSegment[] = item ? [
    { label: grupo.rotulo, href: grupo.itens?.[0]?.caminho, isCurrent: false },
    { label: item.rotulo, href: filho ? caminho : undefined, isCurrent: !filho },
  ] : [{ label: grupo.rotulo, href: filho ? caminho : undefined, isCurrent: !filho }];
  if (filho) {
    const parte = pathname.slice(caminho.length + 1).split('/').at(-1) ?? '';
    const rotulos: Record<string, string> = { novo: new URLSearchParams(search).has('id') ? 'Editar' : 'Novo', detalhe: 'Detalhe', conferencia: 'Conferência', editar: 'Editar' };
    // A rota de conferência termina em UUID; o cabeçalho deve orientar a pessoa.
    const label = /^\/pcp\/recebimentos\/[^/]+$/.test(pathname)
      ? 'Conferência da NF de remessa'
      : pathname === '/cadastros/pecas/nova' ? 'Cadastrar peça'
      : /^\/cadastros\/pecas\/[^/]+$/.test(pathname) ? 'Ficha da peça'
      : rotulos[parte] ?? parte.charAt(0).toUpperCase() + parte.slice(1).replace(/-/g, ' ');
    segmentos.push({ label, isCurrent: true });
  }
  return segmentos;
}

/**
 * Árvore canônica de navegação nos domínios da UI-SPEC (D-04..D-08).
 * Nenhum caminho de coletor (/coletor/*) está presente (D-07).
 */
export const ARVORE_NAVEGACAO: readonly GrupoNavegacao[] = [
  {
    id: 'painel',
    rotulo: 'Painel',
    iconeNome: 'LayoutDashboard',
    caminho: '/painel',
    // Rota inicial do shell; visível para todos os perfis autenticados no shell (D-04)
  },
  {
    id: 'vendas',
    rotulo: 'Vendas',
    iconeNome: 'ClipboardList',
    itens: [
      {
        id: 'vendas-precos',
        rotulo: 'Tabela de Preços',
        caminho: '/vendas/precos',
        permissaoExigida: 'precos:gerenciar',
      },
    ],
  },
  {
    id: 'remessas',
    rotulo: 'Remessas',
    iconeNome: 'Truck',
    caminho: '/remessas',
    permissaoExigida: 'remessas:consultar',
  },
  {
    // Fase 17.3 (D-24): a área "Produção" reúne o que a fábrica usa todo dia, com as palavras do cliente.
    // Rotas e permissões de cada item não mudaram; o grupo aparece quando ao menos um item está visível.
    id: 'producao',
    rotulo: 'Produção',
    iconeNome: 'Factory',
    itens: [
      {
        id: 'vendas-pedidos',
        rotulo: 'Pedidos',
        caminho: '/vendas/pedidos',
        permissaoExigida: 'pv:ler',
      },
      {
        id: 'producao-ordens',
        rotulo: 'Ordens de produção',
        caminho: '/producao/ordens',
        permissaoExigida: 'producao:gerenciar',
      },
      {
        id: 'producao-roteiros',
        rotulo: 'Roteiros',
        caminho: '/pcp/roteiros',
        permissaoExigida: 'producao:consultar',
      },
    ],
  },
  {
    id: 'planejamento',
    rotulo: 'Planejamento',
    iconeNome: 'Calendar',
    itens: [
      {
        id: 'planejamento-gantt',
        rotulo: 'Gantt',
        caminho: '/planejamento/gantt',
        permissaoExigida: 'planejamento:consultar',
      },
      {
        id: 'planejamento-fila',
        rotulo: 'Fila',
        caminho: '/planejamento/fila',
        permissaoExigida: 'planejamento:consultar',
      },
      {
        id: 'planejamento-kanban',
        rotulo: 'Kanban',
        caminho: '/planejamento/kanban',
        permissaoExigida: 'planejamento:consultar',
      },
    ],
  },
  {
    id: 'qualidade',
    rotulo: 'Qualidade',
    iconeNome: 'CheckCircle',
    itens: [
      {
        id: 'qualidade-nc',
        rotulo: 'Não Conformidades',
        caminho: '/qualidade/nao-conformidades',
        permissaoExigida: 'qualidade:gerenciar',
      },
      {
        id: 'qualidade-certificados',
        rotulo: 'Certificados',
        caminho: '/qualidade/certificados',
        permissaoExigida: 'qualidade:gerenciar',
      },
    ],
  },
  {
    id: 'estoque',
    rotulo: 'Estoque',
    iconeNome: 'Boxes',
    // Fase 13: deixa de ser link simples e vira grupo com itens (Saldos, Localizações, Inventários e Consumo de tinta).
    itens: [
      {
        id: 'estoque-saldos',
        rotulo: 'Saldos',
        caminho: '/estoque',
        permissaoExigida: 'estoque:consultar',
      },
      {
        id: 'estoque-abastecimento',
        rotulo: 'Abastecimento',
        caminho: '/estoque/abastecimento',
        permissaoExigida: 'estoque:consultar',
      },
      {
        id: 'estoque-localizacoes',
        rotulo: 'Localizações',
        caminho: '/estoque/localizacoes',
        permissaoExigida: 'estoque:consultar',
      },
      {
        id: 'estoque-inventarios',
        rotulo: 'Inventários',
        caminho: '/estoque/inventarios',
        permissaoExigida: 'estoque:consultar',
      },
      {
        id: 'estoque-consumo-tinta',
        rotulo: 'Consumo de tinta',
        caminho: '/estoque/consumo-tinta',
        permissaoExigida: 'estoque:consultar',
      },
    ],
  },
  {
    id: 'expedicao',
    rotulo: 'Expedição',
    iconeNome: 'Truck',
    caminho: '/expedicao',
    permissaoExigida: 'expedicao:consultar',
  },
  {
    id: 'compras',
    rotulo: 'Compras',
    iconeNome: 'ShoppingCart',
    // Fase 13, Plano 17: grupo logo após Estoque (UI-SPEC Navegação). Plano 22: "Notas de Compra" entre Pedidos e Fornecedores.
    itens: [
      {
        id: 'compras-pedidos',
        rotulo: 'Pedidos de Compra',
        caminho: '/compras/pedidos',
        permissaoExigida: 'compras:consultar',
      },
      {
        id: 'compras-notas',
        rotulo: 'Notas de Compra',
        caminho: '/compras/notas',
        permissaoExigida: 'compras:consultar',
      },
      {
        id: 'compras-fornecedores',
        rotulo: 'Fornecedores',
        caminho: '/compras/fornecedores',
        permissaoExigida: 'compras:consultar',
      },
    ],
  },
  {
    id: 'monitoramento',
    rotulo: 'Monitoramento',
    iconeNome: 'Activity',
    itens: [
      {
        id: 'monitoramento-painel',
        rotulo: 'Tempo real',
        caminho: '/monitoramento/painel',
        permissaoExigida: 'monitoramento:consultar',
      },
      {
        id: 'monitoramento-visao-geral',
        rotulo: 'Visão Geral',
        caminho: '/monitoramento/visao-geral',
        permissaoExigida: 'monitoramento:consultar',
      },
    ],
  },
  {
    id: 'relatorios',
    rotulo: 'Relatórios',
    iconeNome: 'BarChart3',
    itens: [
      {
        id: 'relatorios-index',
        rotulo: 'Relatórios',
        caminho: '/relatorios',
        permissaoExigida: 'relatorios:consultar',
      },
      {
        id: 'relatorios-g7',
        rotulo: 'Relatório G7',
        caminho: '/relatorios/g7',
        permissaoExigida: 'relatorios:consultar',
      },
    ],
  },
  {
    id: 'cadastros',
    rotulo: 'Cadastros',
    iconeNome: 'FolderTree',
    itens: [
      {
        id: 'cadastros-geral',
        rotulo: 'Cadastros',
        caminho: '/cadastros',
        permissaoExigida: 'cadastros:consultar',
      },
      {
        id: 'cadastros-materiais',
        rotulo: 'Materiais',
        caminho: '/cadastros/materiais',
        permissaoExigida: 'cadastros:consultar',
      },
      {
        id: 'cadastros-pecas',
        rotulo: 'Peças / Produtos',
        caminho: '/cadastros/pecas',
        permissaoExigida: 'producao:consultar',
      },
      {
        // Fase 17 (D-10, PORT-UI-20): só aparece com a permissão dedicada; a autorização real é da API (403).
        id: 'cadastros-acessos-portal',
        rotulo: 'Acessos do portal',
        caminho: '/cadastros/acessos-portal',
        permissaoExigida: 'portal:gerenciar-acessos',
      },
    ],
  },
  {
    id: 'omie',
    rotulo: 'Integração Omie',
    iconeNome: 'CloudSync',
    itens: [
      {
        id: 'omie-recebimentos',
        rotulo: 'NF de remessa (Omie)',
        caminho: '/pcp/recebimentos',
        permissaoExigida: 'recebimentos:consultar',
      },
      {
        id: 'omie-clientes',
        rotulo: 'Clientes',
        caminho: '/omie/clientes',
        permissaoExigida: 'omie:consultar',
      },
      {
        id: 'omie-pendencias',
        rotulo: 'Pendências',
        caminho: '/omie/pendencias',
        permissaoExigida: 'omie:consultar',
      },
      {
        // L-63 (17.1-28): fila das pendências operacionais (NF de remessa cancelada na Omie, lote sem roteiro...). Fica neste grupo
        // porque PCP e Faturamento já o usam; a permissão é a mesma de `GET /pendencias`.
        id: 'omie-pendencias-operacionais',
        rotulo: 'Pendências operacionais',
        caminho: '/pendencias',
        permissaoExigida: 'remessas:consultar',
      },
    ],
  },
  {
    id: 'administracao',
    rotulo: 'Administração',
    iconeNome: 'Shield',
    itens: [
      {
        id: 'admin-usuarios',
        rotulo: 'Usuários',
        caminho: '/admin/users',
        permissaoExigida: 'users:manage',
      },
      {
        id: 'admin-perfis',
        rotulo: 'Perfis e permissões',
        caminho: '/admin/perfis',
        permissaoExigida: 'users:manage',
      },
      {
        id: 'admin-configurar-tablets',
        rotulo: 'Configurar tablets',
        caminho: '/coletor/kanban/configurar',
        permissaoExigida: 'users:manage',
      },
    ],
  },
  { id: 'ajuda', rotulo: 'Central de ajuda', iconeNome: 'CircleHelp', caminho: '/ajuda' },
];

/**
 * Filtra a árvore de navegação contra o array de permissões da sessão do usuário.
 * Aplica:
 * - D-04: Painel é sempre visível
 * - D-05: Itens gateados por permissão
 * - D-08: Grupos sem nenhum filho visível desaparecem por completo (sem cabeçalho vazio)
 */
export function filtrarNavegacaoPorPermissoes(
  permissoes: readonly string[] = [],
  arvore: readonly GrupoNavegacao[] = ARVORE_NAVEGACAO,
): readonly GrupoNavegacao[] {
  const permSet = new Set(permissoes);

  const resultado: GrupoNavegacao[] = [];

  for (const grupo of arvore) {
    if (grupo.itens && grupo.itens.length > 0) {
      const itensFiltrados = grupo.itens.filter((item) => {
        if (!item.permissaoExigida) return true;
        return permSet.has(item.permissaoExigida);
      });

      // D-08: grupo sem nenhum filho visível não é incluído
      if (itensFiltrados.length > 0) {
        resultado.push({
          ...grupo,
          itens: itensFiltrados,
        });
      }
    } else {
      // Item simples de nível raiz
      if (!grupo.permissaoExigida || permSet.has(grupo.permissaoExigida)) {
        resultado.push(grupo);
      }
    }
  }

  return resultado;
}
